/**
 * Moodboard layout composable: resolution-agnostic packing with importance ranking.
 * - computePackingLayout: greedy negative-space packing (primary)
 * - computeSpiralLayout: hero–star–fill (legacy fallback)
 */

export interface ImageForLayout {
  width: number
  height: number
  starred?: boolean
  rating?: number | null
  order?: number
}

export interface LayoutRect {
  left: number
  top: number
  width: number
  height: number
}

/** Importance weights for packing layout. */
const STAR_WEIGHT = 1.0
const RATING_WEIGHT = 0.5
const ORDER_WEIGHT = 0.3
const AREA_WEIGHT = 0.2
const PACKING_MARGIN = 0.05

const TIER_SCORE_THRESHOLDS = [1.2, 0.6, 0.2]
/** Border as fraction of canvas (2–5%) when scaling layout to fill. */
const LAYOUT_FILL_MARGIN = 0.035

/**
 * Compute tier size fractions and hero multiplier ranges from an emphasis value (0–1).
 *   emphasis=0: all images nearly equal size (flat hierarchy)
 *   emphasis=1: hero is huge, starred images large, others tiny
 *   emphasis=0.5: roughly the original defaults
 */
function emphasisParams(emphasis: number) {
  const e = Math.max(0, Math.min(1, emphasis))
  // Tier fracs interpolate from flat (all 0.22) to a dramatic spread.
  const flat = 0.22
  const maxFracs = [0.48, 0.26, 0.16, 0.09]
  const tierFracs = maxFracs.map((f) => flat + (f - flat) * e)
  // Hero multiplier ranges (applied on top of tier0 size).
  const heroStarMin = 1.0 + e * 0.8    // 1.0 → 1.8
  const heroStarMax = 1.0 + e * 1.0    // 1.0 → 2.0
  const heroUnstarMin = 1.0 + e * 0.55  // 1.0 → 1.55
  const heroUnstarMax = 1.0 + e * 0.75  // 1.0 → 1.75
  return { tierFracs, heroStarMin, heroStarMax, heroUnstarMin, heroUnstarMax }
}

function computeImportanceScore(
  img: ImageForLayout,
  _index: number,
  images: ImageForLayout[],
  maxArea: number
): number {
  let score = 0
  if (img.starred) score += STAR_WEIGHT
  if (img.rating != null && img.rating >= 1 && img.rating <= 5) {
    score += (img.rating / 5) * RATING_WEIGHT
  }
  if (images.length > 1 && img.order != null) {
    const orders = images.map((i) => i.order ?? 0).filter((o) => o != null)
    const maxOrder = orders.length ? Math.max(...orders) : 0
    const minOrder = orders.length ? Math.min(...orders) : 0
    if (maxOrder > minOrder) score += ((maxOrder - (img.order ?? 0)) / (maxOrder - minOrder)) * ORDER_WEIGHT
    else if (img.order != null) score += ORDER_WEIGHT
  }
  const area = (img.width || 1) * (img.height || 1)
  if (maxArea > 0) score += (area / maxArea) * AREA_WEIGHT
  return score
}

function tierForScore(score: number): number {
  for (let t = 0; t < TIER_SCORE_THRESHOLDS.length; t++) {
    if (score >= TIER_SCORE_THRESHOLDS[t]) return t
  }
  return TIER_SCORE_THRESHOLDS.length
}

function targetSizeFractionForTier(tier: number, tierFracs: number[]): number {
  return tierFracs[Math.min(tier, tierFracs.length - 1)] ?? 0.12
}

function rectsOverlap(a: LayoutRect, b: LayoutRect): boolean {
  return !(a.left + a.width <= b.left || b.left + b.width <= a.left ||
    a.top + a.height <= b.top || b.top + b.height <= a.top)
}

/** Rects in normalized 0-1 space. */
interface NormBounds {
  left: number
  top: number
  right: number
  bottom: number
}

function clampToBounds(left: number, top: number, width: number, height: number, b: NormBounds): LayoutRect {
  let l = Math.max(b.left, Math.min(b.right - width, left))
  let t = Math.max(b.top, Math.min(b.bottom - height, top))
  l = Math.max(b.left, Math.min(b.right - width, l))
  t = Math.max(b.top, Math.min(b.bottom - height, t))
  return { left: l, top: t, width, height }
}

/** Measure open space outward from an edge (0 = blocked, 1 = fully open). */
function measureNegativeSpace(
  anchor: LayoutRect,
  edge: 'left' | 'right' | 'top' | 'bottom',
  placed: LayoutRect[],
  bounds: NormBounds
): number {
  let limit: number
  if (edge === 'left') limit = anchor.left - bounds.left
  else if (edge === 'right') limit = bounds.right - (anchor.left + anchor.width)
  else if (edge === 'top') limit = anchor.top - bounds.top
  else limit = bounds.bottom - (anchor.top + anchor.height)
  for (const p of placed) {
    if (p === anchor) continue
    const gap = edge === 'left' ? anchor.left - (p.left + p.width)
      : edge === 'right' ? p.left - (anchor.left + anchor.width)
      : edge === 'top' ? anchor.top - (p.top + p.height)
      : p.top - (anchor.top + anchor.height)
    if (gap > 0) limit = Math.min(limit, gap)
  }
  return Math.max(0, limit)
}

/** Find max non-overlapping size for a rect placed snug against an anchor edge. Cap by maxShortSide for tier-based visual weight.
 * Tries 3 alignment positions per edge (flush-start, center, flush-end) to reduce corner gaps. */
function placeSnugAgainstEdge(
  anchor: LayoutRect,
  edge: 'left' | 'right' | 'top' | 'bottom',
  aspect: number,
  placed: LayoutRect[],
  bounds: NormBounds,
  maxShortSide: number = 0.6
): LayoutRect | null {
  // Small gap between images so they don't visually touch (in normalized units).
  const GAP = 0.01
  let best: LayoutRect | null = null
  let bestArea = 0
  const step = 0.02
  const maxS = Math.min(0.6, maxShortSide)
  for (let s = step; s <= maxS; s += step) {
    const w = aspect >= 1 ? s * aspect : s
    const h = aspect >= 1 ? s : s / aspect
    // Determine base position for the edge, then try 3 alignments along the perpendicular axis.
    let baseLeft: number, baseTop: number
    if (edge === 'left') {
      baseLeft = anchor.left - w - GAP
      // alignments: flush top, center, flush bottom
      const aligns = [anchor.top, anchor.top + (anchor.height - h) / 2, anchor.top + anchor.height - h]
      for (const t of aligns) {
        const rect = clampToBounds(baseLeft, t, w, h, bounds)
        if (rect.width < 0.01 || rect.height < 0.01) continue
        if (placed.some((p) => rectsOverlap(rect, p))) continue
        const area = rect.width * rect.height
        if (area > bestArea) { bestArea = area; best = rect }
      }
    } else if (edge === 'right') {
      baseLeft = anchor.left + anchor.width + GAP
      const aligns = [anchor.top, anchor.top + (anchor.height - h) / 2, anchor.top + anchor.height - h]
      for (const t of aligns) {
        const rect = clampToBounds(baseLeft, t, w, h, bounds)
        if (rect.width < 0.01 || rect.height < 0.01) continue
        if (placed.some((p) => rectsOverlap(rect, p))) continue
        const area = rect.width * rect.height
        if (area > bestArea) { bestArea = area; best = rect }
      }
    } else if (edge === 'top') {
      baseTop = anchor.top - h - GAP
      const aligns = [anchor.left, anchor.left + (anchor.width - w) / 2, anchor.left + anchor.width - w]
      for (const l of aligns) {
        const rect = clampToBounds(l, baseTop, w, h, bounds)
        if (rect.width < 0.01 || rect.height < 0.01) continue
        if (placed.some((p) => rectsOverlap(rect, p))) continue
        const area = rect.width * rect.height
        if (area > bestArea) { bestArea = area; best = rect }
      }
    } else {
      baseTop = anchor.top + anchor.height + GAP
      const aligns = [anchor.left, anchor.left + (anchor.width - w) / 2, anchor.left + anchor.width - w]
      for (const l of aligns) {
        const rect = clampToBounds(l, baseTop, w, h, bounds)
        if (rect.width < 0.01 || rect.height < 0.01) continue
        if (placed.some((p) => rectsOverlap(rect, p))) continue
        const area = rect.width * rect.height
        if (area > bestArea) { bestArea = area; best = rect }
      }
    }
  }
  return best
}

export interface PackingLayoutOptions {
  randomizeTies?: boolean
  /** When true, apply random center offset and initial scale so Refresh produces visibly different layouts. */
  refreshVariation?: boolean
  centerOffsetX?: number
  centerOffsetY?: number
  initialScaleMultiplier?: number
  /** When set, this image is placed first as hero at 1.2–1.6× starred size (1.6× if starred, else 1.2×). */
  selectedIndex?: number | null
  /**
   * 0–1. Controls size differentiation between importance tiers.
   * 0 = all images roughly equal; 1 = hero huge, starred large, others tiny. Default 0.5.
   */
  emphasis?: number
  /**
   * 0–1. Accumulated refresh strength — grows with each successive Refresh click, resets on close.
   * Progressively expands scale variation range, per-image jitter, and adds a small emphasis nudge.
   */
  refreshStrength?: number
}

/**
 * Greedy negative-space packing layout. Works in normalized 0-1 canvas space.
 * Place most important first; each next image adjacent to existing, toward most negative space.
 * Same importance tier gets similar target size (visual weight).
 */
export function computePackingLayout(
  canvasAspect: number,
  images: ImageForLayout[],
  options?: PackingLayoutOptions
): LayoutRect[] {
  if (images.length === 0) return []

  const strength = Math.max(0, Math.min(1, options?.refreshStrength ?? 0))
  // On refresh, nudge emphasis slightly so tier sizes drift a bit each time.
  const emphasisNudge = options?.refreshVariation ? (Math.random() - 0.5) * strength * 0.25 : 0
  const effectiveEmphasis = Math.max(0, Math.min(1, (options?.emphasis ?? 0.5) + emphasisNudge))
  const { tierFracs, heroStarMin, heroStarMax, heroUnstarMin, heroUnstarMax } =
    emphasisParams(effectiveEmphasis)

  const aspect = Math.max(0.5, Math.min(2, canvasAspect))
  const canvasH = 1 / aspect
  const m = PACKING_MARGIN
  const bounds: NormBounds = { left: m, top: m, right: 1 - m, bottom: canvasH - m }
  const minSide = Math.min(1, canvasH)

  const maxArea = Math.max(...images.map((i) => (i.width || 1) * (i.height || 1)), 1)
  const indexed = images.map((img, i) => {
    const score = computeImportanceScore(img, i, images, maxArea)
    return { img, i, score, tier: tierForScore(score) }
  })
  indexed.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score
    if (options?.randomizeTies) return Math.random() - 0.5
    return a.i - b.i
  })

  const sel = options?.selectedIndex
  if (sel != null && sel >= 0 && sel < images.length) {
    const idx = indexed.findIndex((x) => x.i === sel)
    if (idx > 0) {
      const [hero] = indexed.splice(idx, 1)
      indexed.unshift(hero)
    }
  }

  const rects: LayoutRect[] = new Array(images.length)
  const placed: LayoutRect[] = []

  // Scale multiplier range expands with each successive refresh (strength 0→1 adds ±0→±0.18 more spread).
  const scaleHalfRange = options?.refreshVariation ? 0.12 + strength * 0.18 : 0
  const scaleMult = options?.initialScaleMultiplier ?? (options?.refreshVariation
    ? 1.0 + (Math.random() * 2 - 1) * scaleHalfRange
    : 1)

  // On refresh, shuffle images within each tier so traversal order varies, producing different layouts.
  if (options?.refreshVariation) {
    const heroEntry = indexed[0]
    const rest = indexed.slice(1)
    // Group non-hero images by tier, shuffle within each tier, then reassemble.
    const tierGroups = new Map<number, typeof rest>()
    for (const entry of rest) {
      const t = entry.tier
      if (!tierGroups.has(t)) tierGroups.set(t, [])
      tierGroups.get(t)!.push(entry)
    }
    tierGroups.forEach((group) => {
      for (let j = group.length - 1; j > 0; j--) {
        const k = Math.floor(Math.random() * (j + 1))
        ;[group[j], group[k]] = [group[k]!, group[j]!]
      }
    })
    const shuffled: typeof rest = []
    // Maintain tier order (lower tier = higher importance) but shuffled within each tier.
    const sortedTiers = [...tierGroups.keys()].sort((a, b) => a - b)
    for (const t of sortedTiers) shuffled.push(...(tierGroups.get(t) ?? []))
    indexed.splice(0, indexed.length, heroEntry, ...shuffled)
  }

  const first = indexed[0]
  const img0 = first.img
  const a0 = img0.width && img0.height ? img0.width / img0.height : 1
  const tier0Size = targetSizeFractionForTier(first.tier, tierFracs)
  let heroMult = 1
  if (sel != null && first.i === sel) {
    if (img0.starred) {
      heroMult = heroStarMin + Math.random() * (heroStarMax - heroStarMin)
    } else {
      heroMult = heroUnstarMin + Math.random() * (heroUnstarMax - heroUnstarMin)
    }
  }
  const s0 = minSide * tier0Size * heroMult * scaleMult
  const w0 = a0 >= 1 ? s0 * a0 : s0
  const h0 = a0 >= 1 ? s0 : s0 / a0
  // Hero anchored at canvas center; do not apply centerOffset so it stays at 0.5 ±0%
  const cx = 0.5
  const cy = canvasH / 2
  const r0 = clampToBounds(cx - w0 / 2, cy - h0 / 2, w0, h0, bounds)
  rects[first.i] = r0
  placed.push(r0)

  for (let k = 1; k < indexed.length; k++) {
    const { img, i, tier } = indexed[k]
    const a = img.width && img.height ? img.width / img.height : 1
    const baseShort = minSide * targetSizeFractionForTier(tier, tierFracs)
    // Per-image jitter expands with refresh strength: 10% normally → up to 30% at full strength.
    const jitterRange = options?.refreshVariation ? 0.10 + strength * 0.20 : 0.10
    const jitter = (1 - jitterRange / 2) + Math.random() * jitterRange
    const tierMaxShort = baseShort * jitter
    let best: LayoutRect | null = null
    let bestScore = -1
    const ties: LayoutRect[] = []

    for (const anchor of placed) {
      for (const edge of ['left', 'right', 'top', 'bottom'] as const) {
        const candidate = placeSnugAgainstEdge(anchor, edge, a, placed, bounds, tierMaxShort)
        if (!candidate) continue
        const negSpace = measureNegativeSpace(anchor, edge, placed, bounds)
        const score = candidate.width * candidate.height * (1 + negSpace * 2)
        if (score > bestScore) {
          bestScore = score
          best = candidate
          ties.length = 0
          ties.push(candidate)
        } else if (score === bestScore && best !== null) {
          ties.push(candidate)
        }
      }
    }

    const chosen = (options?.randomizeTies && ties.length > 1)
      ? ties[Math.floor(Math.random() * ties.length)]!
      : best
    if (chosen) {
      rects[i] = chosen
      placed.push(chosen)
    } else {
      // Grid-scan fallback: sample a 10x10 grid and pick the first non-overlapping spot.
      const s = Math.min(minSide * 0.15, tierMaxShort)
      const w = a >= 1 ? s * a : s
      const h = a >= 1 ? s : s / a
      let fallback: LayoutRect | null = null
      const cols = 10
      const rows = 10
      const stepX = (bounds.right - bounds.left - w) / (cols - 1)
      const stepY = (bounds.bottom - bounds.top - h) / (rows - 1)
      outer: for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const l = bounds.left + col * stepX
          const t = bounds.top + row * stepY
          const candidate: LayoutRect = { left: l, top: t, width: w, height: h }
          if (!placed.some((p) => rectsOverlap(candidate, p))) {
            fallback = candidate
            break outer
          }
        }
      }
      const rf = fallback ?? clampToBounds(bounds.left, bounds.top, w, h, bounds)
      rects[i] = rf
      placed.push(rf)
    }
  }

  return scaleRectsToFillCanvas(rects, aspect, LAYOUT_FILL_MARGIN, first.i)
}

/**
 * Scale and translate all rects so the layout fits the canvas (scale-to-fit) with the hero
 * anchored at canvas center. No rect extends outside [0,1]×[0,canvasH], so no images are cropped.
 */
function scaleRectsToFillCanvas(
  rects: LayoutRect[],
  canvasAspect: number,
  marginFraction: number,
  heroIndex: number = 0
): LayoutRect[] {
  if (rects.length === 0) return rects
  const canvasH = 1 / canvasAspect
  const hero = rects[heroIndex] ?? rects[0]
  const heroCenterX = hero.left + hero.width / 2
  const heroCenterY = hero.top + hero.height / 2
  const dx = 0.5 - heroCenterX
  const dy = canvasH / 2 - heroCenterY

  const translated = rects.map((r) => ({
    left: r.left + dx,
    top: r.top + dy,
    width: r.width,
    height: r.height
  }))

  let minL = translated[0].left
  let minT = translated[0].top
  let maxR = translated[0].left + translated[0].width
  let maxB = translated[0].top + translated[0].height
  for (let i = 1; i < translated.length; i++) {
    const r = translated[i]
    if (r.left < minL) minL = r.left
    if (r.top < minT) minT = r.top
    if (r.left + r.width > maxR) maxR = r.left + r.width
    if (r.top + r.height > maxB) maxB = r.top + r.height
  }
  const contentW = maxR - minL
  const contentH = maxB - minT
  const availableW = 1 - 2 * marginFraction
  const availableH = canvasH - 2 * marginFraction
  // Scale-to-fit so all rects stay inside canvas and no images are cropped (was scale-to-fill).
  const scale = Math.min(
    availableW / contentW,
    availableH / contentH,
    4
  )
  const canvasCenterY = canvasH / 2

  return translated.map((r) => ({
    left: 0.5 + (r.left - 0.5) * scale,
    top: canvasCenterY + (r.top - canvasCenterY) * scale,
    width: r.width * scale,
    height: r.height * scale
  }))
}

/**
 * Convert normalized rects (0-1) to position/scale for persistence.
 * positionX, positionY: center 0-1; scale: short side as fraction of canvas short side.
 */
export function rectsToPositionScaleNormalized(
  canvasAspect: number,
  rects: LayoutRect[]
): Array<{ positionX: number; positionY: number; scale: number }> {
  const canvasH = 1 / canvasAspect
  const minSide = Math.min(1, canvasH)
  return rects.map((r) => {
    const centerX = r.left + r.width / 2
    const centerY = r.top + r.height / 2
    const displayShort = Math.min(r.width, r.height)
    return {
      positionX: centerX,
      positionY: centerY * canvasAspect,
      scale: displayShort / minSide
    }
  })
}

/** Inner area: margin 7% on each side (86% of canvas). */
const INNER_MARGIN = 0.07

/** Hero (center) image short side as fraction of canvas short side. */
const FOCAL_SIZE_FRACTION = 0.52
/** Fraction of a region's short side used for each image (leaves padding). */
const REGION_FILL = 0.88

interface Bounds {
  left: number
  top: number
  right: number
  bottom: number
}

function clampRectToBounds(
  left: number,
  top: number,
  width: number,
  height: number,
  bounds: Bounds
): LayoutRect {
  let l = left
  let t = top
  if (l < bounds.left) l = bounds.left
  if (l + width > bounds.right) l = bounds.right - width
  if (t < bounds.top) t = bounds.top
  if (t + height > bounds.bottom) t = bounds.bottom - height
  return { left: l, top: t, width, height }
}

/**
 * Place one image centered in a slot; size by short side to fit; return rect clamped to bounds.
 */
function placeInSlot(
  img: ImageForLayout,
  slotLeft: number,
  slotTop: number,
  slotW: number,
  slotH: number,
  bounds: Bounds
): LayoutRect {
  const aspect = img.width && img.height ? img.width / img.height : 1
  const shortSide = Math.min(slotW, slotH) * REGION_FILL
  const w = aspect >= 1 ? shortSide * aspect : shortSide
  const h = aspect >= 1 ? shortSide : shortSide / aspect
  const left = slotLeft + (slotW - w) / 2
  const top = slotTop + (slotH - h) / 2
  return clampRectToBounds(left, top, w, h, bounds)
}

/**
 * Returns layout rects: hero at center, then starred in four regions around hero, then rest filling gaps.
 * Same API as before for drop-in replacement (focalIndex/randomOffset kept for compatibility).
 */
export function computeSpiralLayout(
  canvasWidth: number,
  canvasHeight: number,
  images: ImageForLayout[],
  _focalIndex: number,
  randomOffset?: { offsetX: number; offsetY: number }
): LayoutRect[] {
  if (images.length === 0) return []

  const n = images.length
  const rects: LayoutRect[] = new Array(n)

  const bounds: Bounds = {
    left: canvasWidth * INNER_MARGIN,
    top: canvasHeight * INNER_MARGIN,
    right: canvasWidth * (1 - INNER_MARGIN),
    bottom: canvasHeight * (1 - INNER_MARGIN)
  }
  const innerWidth = bounds.right - bounds.left
  const innerHeight = bounds.bottom - bounds.top
  const innerMinSide = Math.min(innerWidth, innerHeight)

  const centerX = canvasWidth / 2
  const centerY = canvasHeight / 2
  const offsetX = randomOffset?.offsetX ?? (Math.random() * 2 - 1) * 0.06 * innerWidth
  const offsetY = randomOffset?.offsetY ?? (Math.random() * 2 - 1) * 0.06 * innerHeight
  const layoutCenterX = centerX + offsetX
  const layoutCenterY = centerY + offsetY

  // 1. Hero = largest by area, placed at center
  const indicesByArea = images
    .map((img, i) => ({ i, area: (img.width || 1) * (img.height || 1) }))
    .sort((a, b) => b.area - a.area)
    .map((x) => x.i)

  const heroIndex = indicesByArea[0]
  const hero = images[heroIndex]
  const focalShortSide = innerMinSide * FOCAL_SIZE_FRACTION
  const heroAspect = hero.width && hero.height ? hero.width / hero.height : 1
  const heroW = heroAspect >= 1 ? focalShortSide * heroAspect : focalShortSide
  const heroH = heroAspect >= 1 ? focalShortSide : focalShortSide / heroAspect
  const heroLeft = layoutCenterX - heroW / 2
  const heroTop = layoutCenterY - heroH / 2
  rects[heroIndex] = clampRectToBounds(heroLeft, heroTop, heroW, heroH, bounds)

  if (n === 1) return rects

  // 2. Non-hero: starred first, then by area descending; assign to four regions round-robin
  const nonHeroIndices = indicesByArea.slice(1)
  const starredFirst = [...nonHeroIndices].sort((a, b) => {
    const sa = images[a].starred ? 1 : 0
    const sb = images[b].starred ? 1 : 0
    if (sb !== sa) return sb - sa
    const areaA = (images[a].width || 1) * (images[a].height || 1)
    const areaB = (images[b].width || 1) * (images[b].height || 1)
    return areaB - areaA
  })

  const regionAssignments: number[][] = [[], [], [], []]
  starredFirst.forEach((idx, i) => regionAssignments[i % 4].push(idx))

  // 3. Four regions around the hero. Left/right span only the hero's vertical extent to avoid
  //    corner overlap; top/bottom expand to full canvas width so corners are not wasted.
  const heroRight = heroLeft + heroW
  const heroBottom = heroTop + heroH
  const regions: { left: number; top: number; width: number; height: number }[] = [
    // left strip: full canvas width left of hero, spanning hero's vertical range
    { left: bounds.left, top: heroTop, width: Math.max(0, heroLeft - bounds.left), height: heroH },
    // right strip: full canvas width right of hero, spanning hero's vertical range
    { left: heroRight, top: heroTop, width: Math.max(0, bounds.right - heroRight), height: heroH },
    // top strip: full canvas width, above hero
    { left: bounds.left, top: bounds.top, width: innerWidth, height: Math.max(0, heroTop - bounds.top) },
    // bottom strip: full canvas width, below hero
    { left: bounds.left, top: heroBottom, width: innerWidth, height: Math.max(0, bounds.bottom - heroBottom) }
  ]

  // 4. Layout each region: stack images in the region (vertical for left/right, horizontal for top/bottom)
  for (let r = 0; r < 4; r++) {
    const region = regions[r]
    const indices = regionAssignments[r]
    if (indices.length === 0 || region.width < 4 || region.height < 4) continue

    const isVertical = region.height >= region.width
    const num = indices.length
    if (isVertical) {
      const slotHeight = region.height / num
      indices.forEach((imageIndex, i) => {
        const slotTop = region.top + i * slotHeight
        rects[imageIndex] = placeInSlot(
          images[imageIndex],
          region.left,
          slotTop,
          region.width,
          slotHeight,
          bounds
        )
      })
    } else {
      const slotWidth = region.width / num
      indices.forEach((imageIndex, i) => {
        const slotLeft = region.left + i * slotWidth
        rects[imageIndex] = placeInSlot(
          images[imageIndex],
          slotLeft,
          region.top,
          slotWidth,
          region.height,
          bounds
        )
      })
    }
  }

  return rects
}

const MAGAZINE_MARGIN = 0.035
const MAGAZINE_GUTTER = 0.012

/** Valid row-image-count configurations by total image count. Chosen randomly each generation. */
const ROW_CONFIGS: Record<number, number[][]> = {
  1: [[1]],
  2: [[2], [1, 1]],
  3: [[3], [2, 1], [1, 2]],
  4: [[4], [2, 2], [3, 1], [1, 3], [2, 1, 1]],
  5: [[3, 2], [2, 3], [2, 2, 1], [2, 1, 2], [1, 2, 2], [4, 1], [1, 4]],
  6: [[3, 3], [2, 4], [4, 2], [2, 2, 2], [3, 2, 1], [2, 3, 1], [1, 3, 2]],
  7: [[4, 3], [3, 4], [3, 2, 2], [2, 3, 2], [2, 2, 3], [4, 2, 1]],
  8: [[4, 4], [3, 3, 2], [3, 2, 3], [2, 3, 3], [4, 3, 1], [2, 4, 2]],
}

function randomRowConfig(n: number): number[] {
  const configs = ROW_CONFIGS[n]
  if (configs && configs.length > 0) {
    return configs[Math.floor(Math.random() * configs.length)]!
  }
  // Fallback for large n: split into roughly equal thirds
  const r1 = Math.ceil(n / 3)
  const r2 = Math.ceil((n - r1) / 2)
  const r3 = n - r1 - r2
  return r3 > 0 ? [r1, r2, r3] : [r1, r2]
}

/**
 * Magazine grid layout. Each call generates a different layout via:
 * 1. Random row configuration (from a lookup of valid splits per image count)
 * 2. Random contrast range [minW, maxW] for column weights (~1.3×–2.6× spread)
 * 3. Image importance scores nudge weights within that range; noise adds variety
 * 4. Random row height distribution (first row 45–65% of canvas)
 */
export function computeMagazineLayout(
  canvasAspect: number,
  images: ImageForLayout[],
  options?: { randomizeTies?: boolean; variation?: number }
): LayoutRect[] {
  if (images.length === 0) return []

  const variation = options?.variation ?? 0.4
  const canvasH = 1 / canvasAspect
  const margin = MAGAZINE_MARGIN
  const gutter = MAGAZINE_GUTTER

  const innerLeft = margin
  const innerTop = margin
  const innerW = 1 - 2 * margin
  const innerH = canvasH - 2 * margin

  // Score and sort images by importance.
  const maxArea = Math.max(...images.map((img) => (img.width || 1) * (img.height || 1)), 1)
  const indexed = images.map((img, i) => ({
    img,
    i,
    score: computeImportanceScore(img, i, images, maxArea)
  }))
  indexed.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score
    if (options?.randomizeTies) return Math.random() - 0.5
    return a.i - b.i
  })

  const n = images.length

  // 1. Pick a random row configuration.
  const rowImgCounts = randomRowConfig(n)
  const numRows = rowImgCounts.length

  // 2. Random row height distribution: first row gets 45–65%, rest share what's left.
  const heroFrac = 0.45 + Math.random() * 0.20
  const rowHeightFractions: number[] = new Array(numRows)
  rowHeightFractions[0] = heroFrac
  if (numRows > 1) {
    const remaining = 1 - heroFrac
    // Distribute remaining rows with small per-row noise.
    const rawRest = Array.from({ length: numRows - 1 }, () => 1 + (Math.random() - 0.5) * 0.3)
    const restSum = rawRest.reduce((a, b) => a + b, 0)
    for (let i = 1; i < numRows; i++) rowHeightFractions[i] = remaining * (rawRest[i - 1]! / restSum)
  }

  // 3. Random contrast range for column weights.
  const minW = 0.65 + Math.random() * 0.25   // 0.65–0.90
  const maxW = 1.15 + Math.random() * 0.55   // 1.15–1.70

  const scores = indexed.map((x) => x.score)
  const minScore = Math.min(...scores)
  const maxScore = Math.max(...scores)
  const scoreRange = maxScore - minScore

  // Available height after gutters between rows.
  const totalGutterH = gutter * (numRows - 1)
  const availableH = innerH - totalGutterH

  const rects: LayoutRect[] = new Array(n)
  let imgIdx = 0
  let currentTop = innerTop

  for (let rowIdx = 0; rowIdx < numRows; rowIdx++) {
    const rowCount = rowImgCounts[rowIdx] ?? 0
    if (rowCount === 0) { imgIdx += rowCount; continue }

    const rowHeight = availableH * (rowHeightFractions[rowIdx] ?? 1 / numRows)
    const rowImages = indexed.slice(imgIdx, imgIdx + rowCount)

    // 4. Compute column weights: interpolate in [minW, maxW] by normalized score + noise.
    const rawWeights = rowImages.map((ri) => {
      const normScore = scoreRange > 0 ? (ri.score - minScore) / scoreRange : 0.5
      const target = minW + normScore * (maxW - minW)
      const noise = (Math.random() - 0.5) * variation * (maxW - minW) * 0.7
      return Math.max(minW * 0.6, target + noise)
    })
    const weightSum = rawWeights.reduce((a, b) => a + b, 0)

    const totalGutterW = gutter * (rowCount - 1)
    const availableW = innerW - totalGutterW

    let currentLeft = innerLeft
    for (let colIdx = 0; colIdx < rowCount; colIdx++) {
      const ri = rowImages[colIdx]
      if (!ri) continue
      const cellW = availableW * ((rawWeights[colIdx] ?? 1) / weightSum)
      const cellH = rowHeight

      // Contain image within cell (preserve aspect, center, 92% fill).
      const imgAspect = ri.img.width && ri.img.height ? ri.img.width / ri.img.height : 1
      const cellAspect = cellW / cellH
      const fillFrac = 0.92
      let imgW: number, imgH: number
      if (imgAspect >= cellAspect) {
        imgW = cellW * fillFrac
        imgH = imgW / imgAspect
      } else {
        imgH = cellH * fillFrac
        imgW = imgH * imgAspect
      }

      rects[ri.i] = {
        left: currentLeft + (cellW - imgW) / 2,
        top: currentTop + (cellH - imgH) / 2,
        width: imgW,
        height: imgH
      }
      currentLeft += cellW + gutter
    }

    currentTop += rowHeight + (rowIdx < numRows - 1 ? gutter : 0)
    imgIdx += rowCount
  }

  return rects
}

/**
 * Convert pixel rects to normalized position (center 0-1) and scale for persistence.
 * Scale is expressed as ratio of image's short side to canvas short side.
 */
export function rectsToPositionScale(
  canvasWidth: number,
  canvasHeight: number,
  rects: LayoutRect[],
  _images: ImageForLayout[]
): Array<{ positionX: number; positionY: number; scale: number }> {
  const minSide = Math.min(canvasWidth, canvasHeight)
  return rects.map((r) => {
    const centerX = r.left + r.width / 2
    const centerY = r.top + r.height / 2
    const displayShort = Math.min(r.width, r.height)
    const scale = displayShort / minSide
    return {
      positionX: centerX / canvasWidth,
      positionY: centerY / canvasHeight,
      scale
    }
  })
}
